import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { NextPageContext } from 'next';
import {
  AdDetails,
  AdOrder,
  GetAdDetailsResponse,
  GetAdsResponse,
  NewAdDefaultValues,
  ServiceName,
} from '../types/ads.types';
import {
  GET_AD_DETAILS,
  GET_AD_DETAILS_FOR_EDIT,
  GET_ADS,
} from '../queries/ads.queries';
import { apolloClient } from '../apolloClient';
import { PlatformSource } from '../types/common.types';
import { GetRegionsResponse } from '@/api/types/regions.types';
import {
  CategoryBaseInfo,
  CategoryOrderEnum,
  CategoryScopeEnum,
  GetCategoriesResponse,
} from '@/api/types/categories.types';
import { GET_CATEGORIES } from '@/api/queries/categories.queries';
import { findCategory } from '@/components/AdForm/components/CategorySelect/components/CategorySelectBox.helpers';
import { QUERY, ROOT_CATEGORY_SLUG } from '@/constants/common';
import {
  EDIT_AD_PAGE_SLUG,
  EDIT_AD_PAGE_SLUG_INDEX,
  LEGACY_ID_SLUG_INDEX,
  NEW_AD_PAGE_SLUG,
} from '@/hooks/usePageType/usePageType.constants';
import { AdStatus } from '@/types';
import { EditPopupType } from '@/components/Ad/Settings/Settings.types';
import { getPlatformSource } from '@/utils/getPlatformSource';
import { DeviceType } from '@/utils/getDeviceType';

import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { parseQuery } from '@/hooks/useParseQuery/useParseQuery.helpers';

export async function getLoggingHeaders(
  context: NextPageContext,
  source: PlatformSource
) {
  const userAgent =
    (context.req
      ? context.req?.headers['user-agent']
      : window.navigator.userAgent) || '';
  const referrer =
    (context.req
      ? context.req?.headers.referer || context.req?.headers.referrer
      : document.referrer) || '';

  const ipAddr =
    context.req?.headers['cf-connecting-ip'] ||
    context.req?.headers['x-forwarded-for'] ||
    '';
  const countryCode =
    context.req?.headers['cf-ipcountry'] ||
    context.req?.headers['x-vercel-ip-country'] ||
    '';

  const { signRequest } = await import('@/utils/signRequest');
  const { signature, timestamp } = signRequest(
    ipAddr as string,
    countryCode as string
  );

  return {
    ...(countryCode ? { 'X-Vercel-Client-IPCountry': countryCode } : {}),
    ...(timestamp ? { 'X-Vercel-Timestamp': timestamp.toString() } : {}),
    ...(signature ? { 'X-Vercel-Signature': signature } : {}),
    ...(ipAddr ? { 'X-Vercel-Client-IP': ipAddr } : {}),
    ...(userAgent ? { 'User-Agent': userAgent } : {}),
    ...(referrer ? { Referer: referrer } : {}),
    ...(source && { 'X-Platform': source }),
  };
}

export async function getServerAdDetails(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  deviceType: DeviceType
): Promise<{
  adDetails: AdDetails | null;
}> {
  const isBrowser = typeof window !== 'undefined';
  const isEditPage =
    context.query.slug?.[EDIT_AD_PAGE_SLUG_INDEX] === EDIT_AD_PAGE_SLUG;

  if (isBrowser && !isEditPage) {
    return { adDetails: null };
  }

  const legacyId = context.query.slug?.[LEGACY_ID_SLUG_INDEX] as string;
  const userAgent =
    (context.req
      ? context.req?.headers['user-agent']
      : window.navigator.userAgent) || '';
  const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
  const platformSource = getPlatformSource(deviceType);

  const hasAccessToken = !!context?.query?.[QUERY.accessToken];

  if (
    // If user just logged in, we're not ready to fetch ad details yet
    // Because we have to wait for the user to be authenticated, so we skip the query and make this query on client side when access token is present and set to cookies
    hasAccessToken ||
    !legacyId ||
    legacyId === NEW_AD_PAGE_SLUG
  ) {
    return { adDetails: null };
  }

  const source = isSSRWebView ? PlatformSource.App : platformSource;
  const transitionSourceBlock = context.query[QUERY.transitionSourceBlock];

  const adDetails = await apolloClient<GetAdDetailsResponse>(
    client,
    {
      query: isEditPage ? GET_AD_DETAILS_FOR_EDIT : GET_AD_DETAILS,
      variables: {
        legacyId,
        source,
        ...(typeof transitionSourceBlock === 'string' && {
          transitionSourceBlock,
        }),
      },
      errorPolicy: 'all',
      ...(isBrowser && isEditPage && { fetchPolicy: 'no-cache' }),
      context: {
        headers: isEditPage ? {} : await getLoggingHeaders(context, source),
      },
    },
    context
  );

  const adStatus = adDetails?.data?.adDetails?.status;
  const editLockedUntil = adDetails?.data?.adDetails?.editLockedUntil;

  if (
    isEditPage &&
    (!adStatus || adStatus !== AdStatus.Published || editLockedUntil)
  ) {
    const currentUrl = isBrowser ? window.location.href : context.asPath;
    const adUrl =
      currentUrl?.replace(
        `/${EDIT_AD_PAGE_SLUG}`,
        editLockedUntil
          ? `?set_edit_limit_reached=true#${EditPopupType.Error}` // TODO: remove ?set_edit_limit_reached=true after implementing responsive ad page
          : ''
      ) ?? '';

    if (!isBrowser && context.res) {
      context.res.writeHead(302, { location: adUrl }).end();
    } else {
      window.location.replace(adUrl);
    }
  }

  return {
    adDetails: isEditPage ? adDetails?.data?.adDetails || null : null,
  };
}

export async function getNewAdDefaultValues(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  regionsData: GetRegionsResponse | null = null
): Promise<NewAdDefaultValues | null> {
  const asPath = context.asPath?.split('?')[0].slice(1) || '';

  if (!asPath.endsWith(NEW_AD_PAGE_SLUG)) return null;

  const categoriesData = await apolloClient<GetCategoriesResponse>(
    client,
    {
      query: GET_CATEGORIES,
      variables: {
        scope: CategoryScopeEnum.CREATE_AD,
        orderType: CategoryOrderEnum.POSITION_ASC,
        hasBilingualSearch: true,
      },
      fetchPolicy: 'no-cache',
    },
    context
  );

  const categories = categoriesData?.data?.categories || [];
  const regions = regionsData?.regions || [];
  const defaultRegion = regions?.find(region => region.default);

  const category = findCategory(categories, asPath.replace('/new', ''));

  const isSubcategory = category?.parent?.path !== ROOT_CATEGORY_SLUG;
  const defaultCategory = category && !isSubcategory ? category : null;
  const defaultSubcategory = category && isSubcategory ? category : null;

  return {
    categories,
    defaultRegion,
    defaultCategory,
    defaultSubcategory,
  };
}

export async function getSearchResultsServerData(
  client: ApolloClient<NormalizedCacheObject>,
  context: NextPageContext,
  deviceType: DeviceType,
  currentCategory: CategoryBaseInfo | null,
  regionsData?: GetRegionsResponse | null,
  isVipPage = false,
  isRootCategory = false
): Promise<void> {
  const {
    price,
    regionId,
    propertyOptions,
    userLegacyId,
    isShop,
    keywords,
    order,
  } = parseQuery(context.query, regionsData);
  const userAgent = context.req?.headers['user-agent'] || '';
  const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
  const source = isSSRWebView
    ? PlatformSource.App
    : getPlatformSource(deviceType);
  const filters = {
    categoryId: isRootCategory ? null : currentCategory?.id,
    price,
    regionId,
    propertyOptions,
    userLegacyId,
    isShop,
  };
  const vipChunkSize = isVipPage ? AdsChunkSize.Middle : AdsChunkSize.Small;

  if (process.env.E2E_TEST_MODE === 'true') {
    const [{ getAdsVip }, { getAdsLatest }, { generateRandomCursor }] =
      await Promise.all([
        import('@/__e2e__/mock/adsVip'),
        import('@/__e2e__/mock/adsLatest'),
        import('@/utils/helpers'),
      ]);

    client.writeQuery({
      query: GET_ADS(ServiceName.Vip),
      variables: {
        orderType: AdOrder.Random,
        adKind: ServiceName.Vip,
        first: vipChunkSize,
        keywords,
        filters,
        source,
      },
      data: getAdsVip(generateRandomCursor()),
    });

    if (!isVipPage) {
      client.writeQuery({
        query: GET_ADS(ServiceName.Latest),
        variables: {
          orderType: order,
          first: AdsChunkSize.Middle,
          keywords,
          filters,
          keywordsSource: (
            context.query[QUERY.keywordsSource] as string
          )?.toUpperCase(),
          source,
        },
        data: getAdsLatest(generateRandomCursor(), AdsChunkSize.Middle),
      });
    }

    return;
  }

  const sourceLink = context.req
    ? `${(context.req.headers['x-forwarded-proto'] as string) || 'http'}://${
        (context.req.headers['x-forwarded-host'] as string) ||
        context.req.headers.host
      }${context.asPath}`
    : window.location.href;

  const loggingHeaders = await getLoggingHeaders(context, source);

  await Promise.all([
    apolloClient<GetAdsResponse>(
      client,
      {
        query: GET_ADS(ServiceName.Vip),
        variables: {
          orderType: AdOrder.Random,
          adKind: ServiceName.Vip,
          first: vipChunkSize,
          keywords,
          filters,
          source,
        },
        context: { headers: loggingHeaders },
      },
      context
    ),
    !isVipPage
      ? apolloClient<GetAdsResponse>(
          client,
          {
            query: GET_ADS(ServiceName.Latest),
            variables: {
              orderType: order,
              first: AdsChunkSize.Middle,
              keywords,
              filters,
              keywordsSource: (
                context.query[QUERY.keywordsSource] as string
              )?.toUpperCase(),
              sourceLink,
              source,
            },
            context: { headers: loggingHeaders },
          },
          context
        )
      : null,
  ]);
}
